import { Preferences } from '@capacitor/preferences';
import { getLogger } from './logger';

const logger = getLogger('lastRoute');

const LAST_ROUTE_KEY = 'lastRoute';
// Watchdog: the route we are *currently* trying to restore. Set just before navigating and
// cleared once the app has stabilized. If it's still set on the next launch, the previous
// restore never completed (most likely it crashed), so we skip restoration to avoid a loop.
const RESTORE_PENDING_KEY = 'restorePendingRoute';

// Routes that must never be restored on launch: auth / transient sign-in flows.
const NON_CANDIDATE_PREFIXES = [ '/enter', '/EmailLinkSignIn' ];

// Query params that represent transient UI (modals, wizards, post-login redirects). We strip
// these so a restored route never reopens into a modal or chases a stale redirect.
const TRANSIENT_PARAMS = [ 'modal', 'redirect' ];

function errMessage(err: unknown): string {
    return err instanceof Error ? err.message : JSON.stringify(err);
}

// True while we're navigating to a restored route on launch. _app's "component/location
// mismatch" recovery hack must stand down during this window: a restore intentionally moves
// from ('/', HomePage) to (deep path, deep component), and a transient mismatch there would
// otherwise trigger a full reload to '/' and undo (or loop) the restore.
let restoringRoute = false;
const restoringListeners = new Set<() => void>();
export function isRestoringRoute(): boolean {
    return restoringRoute;
}
export function setRestoringRoute(value: boolean): void {
    if (restoringRoute === value) {
        return;
    }
    restoringRoute = value;
    restoringListeners.forEach(listener => listener());
}
// Lets React components (e.g. the home route) reactively hold a loader while a restore is in
// flight, so HomeAppShell never mounts during the window between launch and the replace.
export function subscribeRestoringRoute(listener: () => void): () => void {
    restoringListeners.add(listener);
    return () => { restoringListeners.delete(listener); };
}

// Normalizes a "pathname?search" string into a route worth restoring, or undefined if it
// isn't a candidate. Pure (no I/O) so it can be unit-tested.
export function sanitizeRoute(pathAndSearch: string | null | undefined): string | undefined {
    if (!pathAndSearch || !pathAndSearch.startsWith('/')) {
        return undefined;
    }
    const queryStart = pathAndSearch.indexOf('?');
    const path = queryStart === -1 ? pathAndSearch : pathAndSearch.slice(0, queryStart);
    const rawQuery = queryStart === -1 ? '' : pathAndSearch.slice(queryStart + 1);

    if (NON_CANDIDATE_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`))) {
        return undefined;
    }

    const params = new URLSearchParams(rawQuery);
    TRANSIENT_PARAMS.forEach(param => params.delete(param));
    const cleanedQuery = params.toString();
    return cleanedQuery ? `${path}?${cleanedQuery}` : path;
}

export async function saveLastRoute(pathAndSearch: string): Promise<void> {
    const route = sanitizeRoute(pathAndSearch);
    if (!route) {
        return;
    }
    try {
        await Preferences.set({ key: LAST_ROUTE_KEY, value: route });
    } catch (err) {
        logger.warn(`saveLastRoute failed: ${errMessage(err)}`);
    }
}

export async function loadLastRoute(): Promise<string | undefined> {
    try {
        const { value } = await Preferences.get({ key: LAST_ROUTE_KEY });
        // Re-sanitize on read in case the candidate rules have tightened since it was saved.
        return sanitizeRoute(value ?? undefined);
    } catch (err) {
        logger.warn(`loadLastRoute failed: ${errMessage(err)}`);
        return undefined;
    }
}

export async function getRestorePending(): Promise<string | undefined> {
    try {
        const { value } = await Preferences.get({ key: RESTORE_PENDING_KEY });
        return value ?? undefined;
    } catch (err) {
        logger.warn(`getRestorePending failed: ${errMessage(err)}`);
        return undefined;
    }
}

export async function markRestorePending(route: string): Promise<void> {
    try {
        await Preferences.set({ key: RESTORE_PENDING_KEY, value: route });
    } catch (err) {
        logger.warn(`markRestorePending failed: ${errMessage(err)}`);
    }
}

export async function clearRestorePending(): Promise<void> {
    try {
        await Preferences.remove({ key: RESTORE_PENDING_KEY });
    } catch (err) {
        logger.warn(`clearRestorePending failed: ${errMessage(err)}`);
    }
}

export type RestorePlan =
    | { action: 'none' }
    | { action: 'skip-crash-loop'; pending: string }
    | { action: 'restore'; route: string };

// Pure decision for what a cold launch should do, given the environment and stored state.
// Kept separate from the hook (which supplies the async/router/timer plumbing) so it can be
// unit-tested directly.
export function planRestore(input: {
    isNative: boolean,
    restoreOnWeb: boolean,
    pathname: string,
    search: string,
    pending: string | undefined,
    savedRoute: string | undefined,
}): RestorePlan {
    // Restoration is a native-launch behavior; on web only when explicitly enabled for testing.
    if (!input.isNative && !input.restoreOnWeb) {
        return { action: 'none' };
    }
    // Only take over a clean launch at the bare home route.
    if (input.pathname !== '/' || input.search) {
        return { action: 'none' };
    }
    // The previous restore never cleared its watchdog -> it likely crashed. Skip this launch.
    if (input.pending) {
        return { action: 'skip-crash-loop', pending: input.pending };
    }
    if (!input.savedRoute || input.savedRoute === '/') {
        return { action: 'none' };
    }
    return { action: 'restore', route: input.savedRoute };
}
